import React, { useCallback, useState } from 'react';
import './Styles.css';
import Modal from '../../../../Elements/ids/Modal';
import Form from '../../../../Elements/Form';
import { formatMessage } from '../../../../Localization/formatMessage';
import Messages from '../../../../Localization/Messages';
import ErrorMessage from '../../../../Elements/ErrorMessage';
import { ServiceClient } from '../../../../Services/ServiceClient';
import EmployerInfoButtons from './Buttons/EmployerInfoButtons';
import TextInput from '../../../../Elements/ids/InputElements/TextInput';
import { createServerErrorMessage, getCommonInputFields, getErrorMessage } from '../../../../Helpers/FormInputHelper';
import { useDebouncedEffect } from '../../../../Helpers/CustomHooks';
import { areFieldsValid, getChangedFieldsForEmployer } from './Functions';
import { PresentationMode } from '../../../../Enums/PresentationMode';
import { useNavigate } from 'react-router-dom';
import CountrySelector from '../../../../Elements/CountrySelector';

export const employerInfoFormId = 'employerInfoFormId';

const EmployerInfo = ({ employer, closeView, createNewEmployer, setEmployer }) => {
  const navigate = useNavigate();
  const [editedEmployer, setEditedEmployer] = useState(employer);
  const [canSubmit, setCanSubmit] = useState(false);
  const [errors, setErrors] = useState([]);
  const [modalMode, setModalMode] = useState(employer.id ? PresentationMode.Edit : PresentationMode.Add);

  const getCommonFields = (propertyName, isMandatory) => {
    return getCommonInputFields(editedEmployer, setEditedEmployer, propertyName, errors, isMandatory);
  };

  /**
   * This makes the cancel button navigate back to search page, since in the "ADD" mode cancel should mean cancel the operation to create a new employer
   */
  const cancelFunction = () => {
    modalMode === PresentationMode.Add ? navigate('/') : closeView();
  };

  const onSubmit = event => {
    event.preventDefault();

    if (modalMode === PresentationMode.Add) {
      ServiceClient.createNewEmployer(
        { ...editedEmployer, countryCode: editedEmployer.country?.code },
        newEmployer => {
          closeView();
          createNewEmployer({ ...newEmployer, teams: [] });
        },
        error => {
          setErrors([createServerErrorMessage(error, employerInfoFormId)]);
          setModalMode(PresentationMode.Error);
          setCanSubmit(false);
        }
      );
      return;
    }

    ServiceClient.updateEmployerInfo(
      { ...editedEmployer, countryCode: editedEmployer.country?.code },
      getChangedFieldsForEmployer(editedEmployer, employer),
      updatedEmployer => {
        setEmployer({ agents: employer.agents, ...updatedEmployer });
        closeView();
      },
      error => {
        setErrors([createServerErrorMessage(error, employerInfoFormId)]);
        setModalMode(PresentationMode.Error);
        setCanSubmit(false);
      }
    );
  };

  const validateEmployerInfo = useCallback(() => {
    const validationErrors = areFieldsValid(editedEmployer);
    setErrors(validationErrors);
    setCanSubmit(
      validationErrors.length === 0
        ? modalMode !== PresentationMode.Add
          ? getChangedFieldsForEmployer(editedEmployer, employer) > 0
          : true
        : false
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedEmployer]);

  useDebouncedEffect(validateEmployerInfo, 500);

  return (
    <Modal
      closeView={cancelFunction}
      titleText={formatMessage(Messages.employerPage_employerInfo_title)}
      buttons={<EmployerInfoButtons closeView={cancelFunction} canSubmit={canSubmit} />}>
      <Form
        formId={employerInfoFormId}
        onSubmit={e => {
          onSubmit(e);
        }}>
        <div className="employer-page_employer-info_container">
          <TextInput label={formatMessage(Messages.common_name)} {...getCommonFields('name', true)} />
          <CountrySelector
            label={formatMessage(Messages.common_country)}
            defaultCountryCode={editedEmployer.country?.code}
            onChange={country => setEditedEmployer({ ...editedEmployer, country })}
            cssClasses={'max-width'}
            isMandatory={true}
          />
          <TextInput label={formatMessage(Messages.common_mobile)} {...getCommonFields('mobile')} />
          <TextInput label={formatMessage(Messages.common_phone)} {...getCommonFields('phone')} />
          <TextInput label={formatMessage(Messages.common_email)} {...getCommonFields('email')} />
          <ErrorMessage message={getErrorMessage(employerInfoFormId, errors)} />
        </div>
      </Form>
    </Modal>
  );
};

export default EmployerInfo;
